// =============================================================================
// FOCUS DETECTION SERVICE - Detecteer kern-intenties op basis van signalen
// =============================================================================
//
// Deze service analyseert een doel en detecteert de onderliggende kern-intentie.
// De logica is gebaseerd op vaste regels en signalen, geen AI-interpretatie.
//
// BELANGRIJK:
// - Er kan maar 1 kern-intentie tegelijk actief zijn
// - Detectie is deterministisch (zelfde input = zelfde output)
// - Resultaten worden nooit automatisch toegepast zonder bevestiging
// - Deze logica is canoniek en mag alleen uitgebreid worden met nieuwe signalen
//
// =============================================================================

import {
  Goal,
  Milestone,
  Action,
  CoreIntent,
  MilestoneCategory,
  INTENT_TO_CATEGORY_MAP,
  GoalFocus,
} from '../types';

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface DetectionSignal {
  signal: string;          // Beschrijving van het signaal
  intent: CoreIntent;      // Kern-intentie waar dit op wijst
  weight: number;          // Gewicht (0-1), hoe sterk is dit signaal
  detected: boolean;       // Is dit signaal aanwezig?
}

export interface DetectionResult {
  intent: CoreIntent;              // Gedetecteerde kern-intentie
  category: MilestoneCategory;     // Afgeleide categorie
  confidence: number;              // 0-1, hoe zeker is deze detectie
  signals: DetectionSignal[];      // Alle gedetecteerde signalen
  reasoning: string;               // Interne redenering (voor logging)
}

// -----------------------------------------------------------------------------
// SIGNAAL DETECTIE
// -----------------------------------------------------------------------------

/**
 * Detecteer signalen in een doel die wijzen op een kern-intentie
 */
function detectSignals(
  goal: Goal,
  milestones: Milestone[],
  actions: Action[]
): DetectionSignal[] {
  const signals: DetectionSignal[] = [];

  // Filter data voor dit doel
  const goalMilestones = milestones.filter(m => m.goalId === goal.id);
  const goalActions = actions.filter(a => a.goalId === goal.id);

  // ===== ONHELDERHEID =====
  // Doel is vaag geformuleerd of heeft geen concrete eindpunten
  signals.push({
    signal: 'Doelbeschrijving ontbreekt of is vaag',
    intent: 'onhelderheid',
    weight: 0.6,
    detected: !goal.description || goal.description.length < 20,
  });

  signals.push({
    signal: 'Geen mijlpalen gedefinieerd',
    intent: 'onhelderheid',
    weight: 0.7,
    detected: goalMilestones.length === 0,
  });

  signals.push({
    signal: 'Type C doel (groot/onbekend)',
    intent: 'onhelderheid',
    weight: 0.5,
    detected: goal.goalType === 'C',
  });

  // ===== OVERBELASTING =====
  // Te veel tegelijk, geen prioritering
  signals.push({
    signal: 'Veel mijlpalen zonder voortgang',
    intent: 'overbelasting',
    weight: 0.7,
    detected: goalMilestones.length > 5 && goalMilestones.every(m => !m.targetDate),
  });

  signals.push({
    signal: 'Veel acties, weinig voltooid',
    intent: 'overbelasting',
    weight: 0.6,
    detected: goalActions.length > 10 && goalActions.filter(a => a.completed).length < 2,
  });

  // ===== ONVOLDOENDE VAARDIGHEID =====
  // Doel vereist vaardigheden die nog niet beheerst worden
  signals.push({
    signal: 'Type B doel (bij anderen gezien, zelf nog niet gedaan)',
    intent: 'onvoldoende_vaardigheid',
    weight: 0.6,
    detected: goal.goalType === 'B',
  });

  signals.push({
    signal: 'Vragenlijst: weet niet hoe',
    intent: 'onvoldoende_vaardigheid',
    weight: 0.7,
    detected: goal.questionnaireAnswers?.knowsHow === 'no',
  });

  // ===== GEBREK AAN STRUCTUUR =====
  // Geen systemen, processen of structuur
  signals.push({
    signal: 'Acties zonder mijlpalen (orphan acties)',
    intent: 'gebrek_aan_structuur',
    weight: 0.5,
    detected: goalActions.filter(a => !a.milestoneId).length > 3,
  });

  signals.push({
    signal: 'Mijlpalen zonder datums',
    intent: 'gebrek_aan_structuur',
    weight: 0.6,
    detected: goalMilestones.length > 0 && goalMilestones.every(m => !m.targetDate),
  });

  // ===== GEBREK AAN EXTERNE FEEDBACK =====
  // Geen validatie van de markt/externe partijen
  signals.push({
    signal: 'Geen sparring sessies',
    intent: 'gebrek_aan_externe_feedback',
    weight: 0.4,
    detected: goal.status === 'committed' && !milestones.some(m => m.fromSparring),
  });

  // Signaal: doel is gecommitteerd maar er zijn geen sparring-sessies geweest
  // Dit is zwak signaal omdat sparring niet verplicht is
  
  // ===== OPSCHALING ZONDER HEFBOOM =====
  // Groei vereist meer tijd/middelen dan beschikbaar
  signals.push({
    signal: 'Lange tijdspanne met weinig structuur',
    intent: 'opschaling_zonder_hefboom',
    weight: 0.5,
    detected: (() => {
      if (!goal.startDate || !goal.endDate) return false;
      const start = new Date(goal.startDate);
      const end = new Date(goal.endDate);
      const daysSpan = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      return daysSpan > 180 && goalMilestones.length < 3; // >6 maanden, weinig mijlpalen
    })(),
  });

  // ===== UITSTEL / VERMIJDING =====
  // Doel wordt vermeden of acties worden niet uitgevoerd
  signals.push({
    signal: 'Doel is draft (nog niet gecommitteerd)',
    intent: 'uitstel_vermijding',
    weight: 0.8,
    detected: goal.status === 'draft',
  });

  signals.push({
    signal: 'Geen acties voltooid',
    intent: 'uitstel_vermijding',
    weight: 0.6,
    detected: goalActions.length > 0 && goalActions.every(a => !a.completed),
  });

  signals.push({
    signal: 'Startdatum in verleden maar geen voortgang',
    intent: 'uitstel_vermijding',
    weight: 0.7,
    detected: (() => {
      if (!goal.startDate) return false;
      const start = new Date(goal.startDate);
      const now = new Date();
      return start < now && goalActions.filter(a => a.completed).length === 0;
    })(),
  });

  return signals;
}

// -----------------------------------------------------------------------------
// INTENTIE DETECTIE (met voorrangsregels)
// -----------------------------------------------------------------------------

/**
 * Selecteer de dominante kern-intentie op basis van voorrangsregels
 * 
 * REGELS:
 * 1. Onhelderheid en Overbelasting hebben ALTIJD voorrang (1 trigger = actief)
 * 2. Als beide triggers hebben: Onhelderheid wint
 * 3. Andere intenties: minimum 2 triggers nodig
 * 4. Als geen intentie actief: return null
 * 
 * @param signals - Alle gedetecteerde signalen
 * @returns CoreIntent of null als geen intentie actief is
 */
function selectDominantIntent(signals: DetectionSignal[]): CoreIntent | null {
  // STAP 1: Check voorrangsintenten (Onhelderheid en Overbelasting)
  // Deze hebben slechts 1 trigger nodig om actief te zijn
  const hasOnhelderheid = signals.some(s => s.detected && s.intent === 'onhelderheid');
  const hasOverbelasting = signals.some(s => s.detected && s.intent === 'overbelasting');
  
  // Voorrang: Onhelderheid > Overbelasting
  if (hasOnhelderheid) return 'onhelderheid';
  if (hasOverbelasting) return 'overbelasting';
  
  // STAP 2: Check andere intenties (meerdere triggers nodig)
  // Tel aantal triggers per intent
  const triggerCounts: Record<string, number> = {};
  signals.forEach(s => {
    if (s.detected && s.intent !== 'onhelderheid' && s.intent !== 'overbelasting') {
      triggerCounts[s.intent] = (triggerCounts[s.intent] || 0) + 1;
    }
  });
  
  // Filter intenties met 2+ triggers
  const qualifiedIntents = Object.entries(triggerCounts)
    .filter(([_, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1]); // Meeste triggers eerst
  
  if (qualifiedIntents.length > 0) {
    return qualifiedIntents[0][0] as CoreIntent;
  }
  
  // STAP 3: Geen intentie actief
  return null;
}

/**
 * Bereken confidence score (0-1)
 * Gebaseerd op aantal triggers en of het een voorrangsintentie is
 * 
 * ⚠️ BELANGRIJK: Deze score is ALLEEN voor interne heuristiek en debugging.
 * 
 * GEBRUIK VOOR:
 * - Logging/debugging (welke detectie was "sterker"?)
 * - Interne thresholds (alleen bij zeer lage confidence fallback toepassen)
 * - Development insights
 * 
 * NIET GEBRUIKEN VOOR:
 * - UI feedback ("85% zeker!") ❌
 * - Gebruikerscommunicatie ❌
 * - Absolute claims over correctheid ❌
 * 
 * De score is een relatieve indicator binnen deze detectie-run en heeft
 * geen absolute betekenis. Het is GEEN statistisch bewezen nauwkeurigheid.
 */
function calculateConfidence(
  signals: DetectionSignal[],
  dominantIntent: CoreIntent | null
): number {
  if (!dominantIntent) return 0;

  // Tel triggers voor de dominante intentie
  const triggerCount = signals.filter(s => 
    s.detected && s.intent === dominantIntent
  ).length;

  // Voorrangsintenten (onhelderheid, overbelasting) krijgen hogere confidence
  const isPriorityIntent = dominantIntent === 'onhelderheid' || dominantIntent === 'overbelasting';
  
  if (isPriorityIntent) {
    // 1 trigger = 0.7, 2+ triggers = 0.9+
    return Math.min(0.7 + (triggerCount - 1) * 0.1, 1.0);
  } else {
    // Andere intenties: 2 triggers = 0.6, 3+ triggers = 0.8+
    return Math.min(0.6 + (triggerCount - 2) * 0.1, 1.0);
  }
}

// -----------------------------------------------------------------------------
// PUBLIC API
// -----------------------------------------------------------------------------

/**
 * Detecteer de kern-intentie en afgeleide categorie voor een doel
 * 
 * @param goal - Het doel om te analyseren
 * @param milestones - Alle mijlpalen (worden gefilterd op goalId)
 * @param actions - Alle acties (worden gefilterd op goalId)
 * @returns DetectionResult met intentie, categorie, signalen en confidence, of null als geen intentie actief
 */
export function detectGoalFocus(
  goal: Goal,
  milestones: Milestone[],
  actions: Action[]
): DetectionResult | null {
  // Stap 1: Detecteer alle signalen
  const signals = detectSignals(goal, milestones, actions);

  // Stap 2: Selecteer dominante intentie (met voorrangsregels)
  const intent = selectDominantIntent(signals);

  // Stap 3: Als geen intentie actief, return null
  if (!intent) {
    return null;
  }

  // Stap 4: Leid categorie af via vaste mapping
  const category = INTENT_TO_CATEGORY_MAP[intent];

  // Stap 5: Bereken confidence
  const confidence = calculateConfidence(signals, intent);

  // Stap 6: Genereer reasoning (voor logging)
  const detectedSignals = signals.filter(s => s.detected);
  const reasoning = `Detected ${detectedSignals.length} signals pointing to '${intent}' (confidence: ${(confidence * 100).toFixed(0)}%)`;

  return {
    intent,
    category,
    confidence,
    signals,
    reasoning,
  };
}

/**
 * Converteer DetectionResult naar GoalFocus object (klaar voor Firebase)
 * 
 * @param goalId - ID van het doel
 * @param ownerId - ID van de eigenaar
 * @param result - DetectionResult van detectGoalFocus()
 * @returns GoalFocus object klaar voor opslag
 */
export function createFocusFromDetection(
  goalId: string,
  ownerId: string,
  result: DetectionResult
): Omit<GoalFocus, 'id'> {
  return {
    goalId,
    ownerId,
    coreIntent: result.intent,
    category: result.category,
    detectedAt: Date.now(),
    confirmedByUser: false, // Moet door gebruiker bevestigd worden
    autoDetected: true,
    detectionSignals: result.signals
      .filter(s => s.detected)
      .map(s => s.signal),
    confidenceScore: result.confidence,
  };
}

/**
 * Helper: Krijg de categorie voor een gegeven intentie (via vaste mapping)
 */
export function getCategoryForIntent(intent: CoreIntent): MilestoneCategory {
  return INTENT_TO_CATEGORY_MAP[intent];
}

/**
 * Helper: Check of een mijlpaal past bij een focus-categorie
 */
export function milestoneMatchesFocus(
  milestone: Milestone,
  focus: GoalFocus
): boolean {
  // Als mijlpaal geen categorie heeft, matcht het niet
  if (!('category' in milestone)) return false;
  
  const categorizedMilestone = milestone as any;
  return categorizedMilestone.category === focus.category;
}
